import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  Play,
  Filter,
  Search,
  Clock,
  Eye,
  CheckCircle2,
  Plus,
  GraduationCap,
  Loader2,
  AlertCircle
} from 'lucide-react';
import {
  trainingService,
  trainingCategoryService,
  trainingStatsService,
  Training,
  TrainingCategory
} from '../../services/supabaseFormation';

interface FormationHubProps {
  memberId: string;
}

// Composant CompactFormationCard
const CompactFormationCard: React.FC<{
  formation: Training & { userCompleted?: boolean };
  onViewDetails: (formation: Training) => void;
}> = ({ formation, onViewDetails }) => {
  const getStatusColor = (completed: boolean) => {
    if (completed) return 'bg-primary-green/10 text-primary-green border border-primary-green/20';
    return 'bg-primary-blue/10 text-primary-blue border border-primary-blue/20';
  };

  const getStatusIcon = (completed: boolean) => {
    if (completed) return <CheckCircle2 size={14} />;
    return <Play size={14} />;
  };

  const getStatusText = (completed: boolean) => {
    if (completed) return 'Terminee';
    return 'Disponible';
  };

  const getDifficultyLabel = (level: string) => {
    switch (level) {
      case 'beginner': return 'Debutant';
      case 'intermediate': return 'Intermediaire';
      case 'advanced': return 'Avance';
      default: return level;
    }
  };

  const category = formation.category as TrainingCategory | undefined;

  return (
    <div
      className="group card-udb p-6 hover:shadow-udb-lg hover:scale-105 transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-primary-blue/30"
      onClick={() => onViewDetails(formation)}
    >
      {/* Thumbnail */}
      {formation.thumbnail_url && (
        <div className="relative mb-4 -mx-6 -mt-6">
          <img
            src={formation.thumbnail_url}
            alt={formation.title}
            className="w-full h-32 object-cover"
          />
          {formation.duration_minutes && (
            <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
              {formation.duration_minutes} min
            </span>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-medium text-gray-900 text-base line-clamp-2 leading-tight mb-3 group-hover:text-primary-blue transition-colors">
            {formation.title}
          </h3>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium ${getStatusColor(formation.userCompleted || false)}`}>
              {getStatusIcon(formation.userCompleted || false)}
              {getStatusText(formation.userCompleted || false)}
            </span>
            {formation.featured && (
              <span className="bg-primary-yellow/20 text-primary-blue text-xs px-2 py-1 font-medium flex items-center gap-1">
                <span>*</span>
                Vedette
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="space-y-3 mb-4">
        {category && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span
              className="w-3 h-3 flex-shrink-0"
              style={{ backgroundColor: category.color }}
            ></span>
            <span className="truncate font-medium">{category.name}</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Clock size={14} className="text-primary-blue flex-shrink-0" />
          <span className="truncate">{formation.duration_minutes || 0} min</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Eye size={14} className="text-primary-blue flex-shrink-0" />
          <span className="truncate">{(formation.view_count || 0).toLocaleString()} vues</span>
        </div>
      </div>

      {/* Progress bar or completion */}
      <div className="space-y-2 mb-3">
        {formation.userCompleted ? (
          <div className="flex items-center gap-2 text-sm text-primary-green">
            <CheckCircle2 size={16} />
            <span className="font-medium">Formation completee</span>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span className="font-medium">Difficulte</span>
              <span className="font-semibold text-primary-blue">{getDifficultyLabel(formation.difficulty_level)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const FormationHub: React.FC<FormationHubProps> = ({ memberId }) => {
  const [activeTab, setActiveTab] = useState('available');
  const [searchQuery, setSearchQuery] = useState('');
  const [formations, setFormations] = useState<(Training & { userCompleted?: boolean })[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [completedFormations, setCompletedFormations] = useState<Training[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [categories, setCategories] = useState<TrainingCategory[]>([]);
  const [stats, setStats] = useState({ totalCompleted: 0, totalHoursWatched: 0, certificatesEarned: 0 });
  const [globalStats, setGlobalStats] = useState({ totalTrainings: 0, totalViews: 0, totalHours: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Charger les donnees
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Charger en parallele
      const [
        { trainings },
        completed,
        categoriesData,
        memberStats,
        globalStatsData
      ] = await Promise.all([
        trainingService.getPublished(),
        memberId ? trainingService.getCompletedByMember(memberId) : Promise.resolve([]),
        trainingCategoryService.getAll(),
        memberId ? trainingStatsService.getMemberStats(memberId) : Promise.resolve({ totalCompleted: 0, totalHoursWatched: 0, certificatesEarned: 0 }),
        trainingStatsService.getGlobalStats()
      ]);

      // Marquer les formations completees
      const completedIds = new Set(completed.map(c => c.id));
      const formationsWithStatus = trainings.map(f => ({
        ...f,
        userCompleted: completedIds.has(f.id)
      }));

      setFormations(formationsWithStatus);
      setCompletedFormations(completed);
      setCategories(categoriesData);
      setStats(memberStats);
      setGlobalStats(globalStatsData);
    } catch (err) {
      console.error('Erreur chargement formations:', err);
      setError('Impossible de charger les formations. Veuillez reessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormationClick = (formation: Training) => {
    // TODO: Ouvrir la modal de details ou naviguer vers la page de formation
    console.log('Ouvrir formation:', formation);
    // Enregistrer la vue
    if (memberId) {
      trainingService.recordView(formation.id, memberId).catch(console.error);
    }
  };

  // Filtrer les formations
  const filteredFormations = formations.filter(formation => {
    const matchesTab =
      activeTab === 'all' ||
      (activeTab === 'available' && !formation.userCompleted) ||
      (activeTab === 'completed' && formation.userCompleted);

    const matchesSearch =
      !searchQuery ||
      formation.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      formation.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (formation.category as TrainingCategory)?.name.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesTab && matchesSearch;
  });

  const statsDisplay = [
    { label: 'Formations disponibles', value: globalStats.totalTrainings.toString(), icon: BookOpen },
    { label: 'Formations completees', value: stats.totalCompleted.toString(), icon: CheckCircle2 },
    { label: 'Heures de formation', value: globalStats.totalHours.toString(), icon: Clock },
    { label: 'Certificats obtenus', value: stats.certificatesEarned.toString(), icon: GraduationCap }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary-blue animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement des formations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-900 font-medium mb-2">Erreur</p>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadData}
            className="btn-udb-primary"
          >
            Reessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Header Section - Style UDB */}
      <section className="relative bg-primary-blue text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full animate-float-up"
                style={{
                  width: Math.random() * 3 + 1 + 'px',
                  height: Math.random() * 3 + 1 + 'px',
                  left: Math.random() * 100 + '%',
                  bottom: '-20px',
                  backgroundColor: i % 3 === 0 ? '#ffd400' : i % 3 === 1 ? '#ffffff' : '#00469b',
                  opacity: Math.random() * 0.6 + 0.2,
                  animationDuration: `${Math.random() * 15 + 10}s`,
                  animationDelay: `${Math.random() * 10}s`,
                }}
              />
            ))}
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-light text-white">
                Apprendre pour
                <span className="text-primary-yellow font-normal ml-2">mieux servir</span>
              </h1>
              <p className="text-white/80 mt-1 font-light">
                Formations citoyennes et politiques de qualite
              </p>
            </div>

            <div className="hidden md:flex items-center gap-6">
              <div className="text-right">
                <div className="text-2xl font-light text-primary-yellow">
                  {globalStats.totalTrainings}
                </div>
                <div className="text-white/60 text-sm">Disponibles</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-light text-primary-yellow">
                  {globalStats.totalHours}
                </div>
                <div className="text-white/60 text-sm">Heures total</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation par onglets */}
      <div className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('available')}
              className={`py-6 px-2 border-b-2 font-medium text-base transition-colors flex items-center gap-2 ${
                activeTab === 'available'
                  ? 'border-primary-blue text-primary-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Play size={18} />
              Disponibles
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`py-6 px-2 border-b-2 font-medium text-base transition-colors flex items-center gap-2 ${
                activeTab === 'completed'
                  ? 'border-primary-green text-primary-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CheckCircle2 size={18} />
              Completees
            </button>
            <button
              onClick={() => setActiveTab('all')}
              className={`py-6 px-2 border-b-2 font-medium text-base transition-colors flex items-center gap-2 ${
                activeTab === 'all'
                  ? 'border-primary-yellow text-primary-yellow'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <GraduationCap size={18} />
              Toutes
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8 -mt-4">
          {statsDisplay.map((stat, index) => (
            <div key={index} className="card-udb p-6 text-center hover:shadow-udb-lg hover:scale-105 transition-all duration-300">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 bg-primary-blue/10 rounded-full flex items-center justify-center">
                  <stat.icon className="text-primary-blue" size={24} />
                </div>
              </div>
              <div className="text-2xl font-light text-primary-blue mb-1">{stat.value}</div>
              <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Search and Filters */}
        <div className="card-udb p-8 mb-8">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher une formation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-100 focus:border-primary-blue outline-none transition-all bg-white/70"
              />
            </div>
            <div className="flex items-center gap-2">
              <button className="flex items-center gap-2 px-6 py-3 border-2 border-gray-100 hover:border-primary-green hover:text-primary-green transition-all bg-white/70">
                <Filter size={20} />
                Filtres
              </button>
              {activeTab === 'available' && (
                <button className="btn-udb-primary flex items-center gap-2">
                  <Plus size={20} />
                  Proposer une formation
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Formations vedettes */}
        {activeTab === 'available' && filteredFormations.filter(f => f.featured && !f.userCompleted).length > 0 && (
          <div className="mb-12">
            <div className="flex items-center space-x-2 mb-8">
              <GraduationCap className="w-6 h-6 text-primary-yellow" />
              <span className="text-primary-blue text-sm uppercase tracking-wider font-semibold">Formations vedettes</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8 leading-tight">
              Developpez vos <span className="text-primary-blue font-normal">competences</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredFormations.filter(f => f.featured && !f.userCompleted).map(formation => (
                <CompactFormationCard
                  key={formation.id}
                  formation={formation}
                  onViewDetails={handleFormationClick}
                />
              ))}
            </div>
          </div>
        )}

        {/* Toutes les formations */}
        <div>
          <h2 className="text-2xl font-light text-gray-900 mb-8">
            {activeTab === 'all' ? 'Toutes les formations' :
             activeTab === 'available' ? 'Formations disponibles' : 'Formations completees'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredFormations.map(formation => (
              <CompactFormationCard
                key={formation.id}
                formation={formation}
                onViewDetails={handleFormationClick}
              />
            ))}
          </div>

          {filteredFormations.length === 0 && (
            <div className="text-center py-12">
              <BookOpen className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune formation trouvee</h3>
              <p className="text-gray-600">
                {searchQuery
                  ? `Aucune formation ne correspond a "${searchQuery}"`
                  : "Aucune formation disponible dans cette categorie"
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormationHub;
